import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useAuth, useLanguage, API } from "@/App";
import { toast } from "sonner";
import axios from "axios";
import { Eye, EyeOff, Mail, Lock, User, Phone } from "lucide-react";

export default function RegisterPage() {
  const [searchParams] = useSearchParams();
  const initialRole = searchParams.get("role") || "user";
  
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [role, setRole] = useState(initialRole);
  const [showPassword, setShowPassword] = useState(false);
  const [acceptTerms, setAcceptTerms] = useState(false);   // NEW — gate registration on legal acceptance
  const [loading, setLoading] = useState(false);
  const { login } = useAuth();
  const { t } = useLanguage();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!name || !email || !password || !confirmPassword) {
      toast.error(t("يرجى ملء جميع الحقول", "Please fill all fields"));
      return;
    }
    if (password !== confirmPassword) {
      toast.error(t("كلمات المرور غير متطابقة", "Passwords don't match"));
      return;
    }
    if (password.length < 6) {
      toast.error(t("كلمة المرور يجب أن تكون 6 أحرف على الأقل", "Password must be at least 6 characters"));
      return;
    }
    if (!acceptTerms) {
      toast.error(t(
        "يجب الموافقة على شروط الاستخدام وسياسة الخصوصية",
        "You must accept the Terms of Service and Privacy Policy"
      ));
      return;
    }

    setLoading(true);
    try {
      const response = await axios.post(`${API}/auth/register`, {
        name, email, password, phone, role,
        accept_terms: acceptTerms,
      }, { withCredentials: true });

      login(response.data.user, response.data.access_token);
      toast.success(t("تم إنشاء الحساب بنجاح!", "Account created successfully!"));
      
      if (role === "driver") {
        navigate("/driver-application");
      } else {
        navigate("/dashboard");
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || t("فشل إنشاء الحساب", "Registration failed"));
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleLogin = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + '/dashboard';
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-950 px-4 py-8">
      <div className="glass-card rounded-2xl p-8 w-full max-w-md animate-fade-in" data-testid="register-form">
        <div className="text-center mb-8">
          <Link to="/" className="inline-flex items-center gap-2 mb-6">
            <div className="w-12 h-12 bg-yellow-500 rounded-xl flex items-center justify-center">
              <span className="text-black font-bold text-xl">س</span>
            </div>
          </Link>
          <h1 className="text-2xl font-bold text-white">{t("إنشاء حساب جديد", "Create Account")}</h1>
          <p className="text-slate-400 mt-2">{t("انضم لمجتمع المغتربين العرب", "Join the Arab expat community")}</p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label className="text-slate-300">{t("نوع الحساب", "Account Type")}</Label>
            <Select value={role} onValueChange={setRole}>
              <SelectTrigger className="h-12 bg-slate-900 border-slate-700 text-white">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-slate-900 border-slate-700">
                <SelectItem value="user">{t("مستخدم عادي", "Regular User")}</SelectItem>
                <SelectItem value="driver">{t("سائق", "Driver")}</SelectItem>
                <SelectItem value="property_owner">{t("مالك عقار", "Property Owner")}</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label className="text-slate-300">{t("الاسم الكامل", "Full Name")}</Label>
            <div className="relative">
              <Input
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder={t("أحمد محمد", "Ahmed Mohamed")}
                className="h-12 bg-slate-900 border-slate-700 text-white pr-10"
                data-testid="register-name"
              />
              <User className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500" size={18} />
            </div>
          </div>

          <div className="space-y-2">
            <Label className="text-slate-300">{t("البريد الإلكتروني", "Email")}</Label>
            <div className="relative">
              <Input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="email@example.com"
                className="h-12 bg-slate-900 border-slate-700 text-white pr-10"
                data-testid="register-email"
              />
              <Mail className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500" size={18} />
            </div>
          </div>

          <div className="space-y-2">
            <Label className="text-slate-300">{t("رقم الهاتف", "Phone")} ({t("اختياري", "optional")})</Label>
            <div className="relative">
              <Input
                type="tel"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                placeholder="+1 (555) 000-0000"
                className="h-12 bg-slate-900 border-slate-700 text-white pr-10"
              />
              <Phone className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500" size={18} />
            </div>
          </div>

          <div className="space-y-2">
            <Label className="text-slate-300">{t("كلمة المرور", "Password")}</Label>
            <div className="relative">
              <Input
                type={showPassword ? "text" : "password"}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••"
                className="h-12 bg-slate-900 border-slate-700 text-white pr-10 pl-10"
                data-testid="register-password"
              />
              <Lock className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500" size={18} />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500"
              >
                {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
              </button>
            </div>
          </div>

          <div className="space-y-2">
            <Label className="text-slate-300">{t("تأكيد كلمة المرور", "Confirm Password")}</Label>
            <Input
              type={showPassword ? "text" : "password"}
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              placeholder="••••••••"
              className="h-12 bg-slate-900 border-slate-700 text-white"
              data-testid="register-confirm-password"
            />
          </div>

          {/* Terms & Privacy acceptance — required before account can be created.
              Both labels are clickable links that open the policy pages in a new tab. */}
          <div className="flex items-start gap-2 pt-1" data-testid="accept-terms-wrap">
            <Checkbox
              id="accept-terms"
              checked={acceptTerms}
              onCheckedChange={(v) => setAcceptTerms(v === true)}
              className="mt-0.5 border-slate-600 data-[state=checked]:bg-yellow-500 data-[state=checked]:border-yellow-500 data-[state=checked]:text-black"
              data-testid="accept-terms-checkbox"
            />
            <Label htmlFor="accept-terms" className="text-xs sm:text-sm text-slate-300 leading-snug cursor-pointer select-none">
              {t("أوافق على", "I agree to the")}{" "}
              <Link
                to="/terms"
                target="_blank"
                rel="noopener noreferrer"
                className="text-yellow-400 font-semibold hover:underline"
                data-testid="terms-link"
              >
                {t("شروط الاستخدام", "Terms of Service")}
              </Link>{" "}
              {t("و", "and")}{" "}
              <Link
                to="/privacy"
                target="_blank"
                rel="noopener noreferrer"
                className="text-yellow-400 font-semibold hover:underline"
                data-testid="privacy-link"
              >
                {t("سياسة الخصوصية", "Privacy Policy")}
              </Link>
            </Label>
          </div>

          <Button type="submit" disabled={loading || !acceptTerms} className="w-full btn-primary h-12 mt-2" data-testid="register-submit">
            {loading ? t("جاري الإنشاء...", "Creating...") : t("إنشاء حساب", "Create Account")}
          </Button>
        </form>

        <div className="relative my-6">
          <div className="absolute inset-0 flex items-center">
            <div className="w-full border-t border-slate-700"></div>
          </div>
          <div className="relative flex justify-center text-sm">
            <span className="bg-slate-900 px-4 text-slate-500">{t("أو", "or")}</span>
          </div>
        </div>

        <Button type="button" onClick={handleGoogleLogin} className="w-full btn-secondary h-12" data-testid="google-register-btn">
          <svg className="w-5 h-5 ml-2" viewBox="0 0 24 24">
            <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
            <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
            <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
            <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
          </svg>
          {t("التسجيل مع Google", "Register with Google")}
        </Button>

        <p className="text-center mt-6 text-slate-400">
          {t("لديك حساب بالفعل؟", "Already have an account?")}{" "}
          <Link to="/login" className="text-yellow-500 font-semibold hover:underline">
            {t("سجل دخولك", "Login")}
          </Link>
        </p>
      </div>
    </div>
  );
}
